import { Extension } from '@tiptap/core';

/**
 * ParagraphSpacing
 * ----------------
 * Keeps Word's paragraph "Spacing Before / After" and first-line / hanging
 * indent on paragraphs and headings. Without these attributes the Tiptap
 * schema silently dropped `margin-top`, `margin-bottom` and `text-indent`, so
 * text copied from Word lost its paragraph spacing and indents on paste.
 *
 * Attributes default to null, so documents that never set them (every letter
 * saved before this extension existed) parse and render exactly as before.
 */

const LENGTH_RE = /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:pt|px|in|cm|mm|em|rem|pc)$/i;

const readLength = (value: string | null | undefined, allowNegative: boolean): string | null => {
  const v = String(value || '').trim();
  if (!v || !LENGTH_RE.test(v)) return null;
  if (!allowNegative && v.startsWith('-')) return null;
  return v;
};

export const ParagraphSpacing = Extension.create({
  name: 'paragraphSpacing',

  addOptions() {
    return { types: ['paragraph', 'heading'] as string[] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          spaceBefore: {
            default: null,
            parseHTML: (el: HTMLElement) => readLength(el.style.marginTop, false),
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.spaceBefore ? { style: `margin-top: ${attrs.spaceBefore}` } : {}
          },
          spaceAfter: {
            default: null,
            parseHTML: (el: HTMLElement) => readLength(el.style.marginBottom, false),
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.spaceAfter ? { style: `margin-bottom: ${attrs.spaceAfter}` } : {}
          },
          textIndent: {
            default: null,
            parseHTML: (el: HTMLElement) => readLength(el.style.textIndent, true),
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.textIndent ? { style: `text-indent: ${attrs.textIndent}` } : {}
          }
        }
      }
    ];
  }
});
